import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

export default api;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ------------- Currency preference -------------
export const CURRENCY_META = {
  USD: { symbol: "$", locale: "en-US", decimals: 2 },
  CAD: { symbol: "C$", locale: "en-CA", decimals: 2 },
};

let _currency = (typeof localStorage !== "undefined" && localStorage.getItem("cashflow.currency")) || "USD";
const _currencyListeners = new Set();

export function getCurrency() {
  return _currency;
}

export function setCurrency(code) {
  if (!code || !CURRENCY_META[code] || code === _currency) return;
  _currency = code;
  try { localStorage.setItem("cashflow.currency", code); } catch { /* noop */ }
  _currencyListeners.forEach((cb) => cb(code));
}

export function subscribeCurrency(cb) {
  _currencyListeners.add(cb);
  return () => _currencyListeners.delete(cb);
}

export function fmtMoney(n) {
  if (n == null || isNaN(n)) n = 0;
  const meta = CURRENCY_META[_currency] || CURRENCY_META.USD;
  const v = Number(n);
  const sign = v < 0 ? "-" : "";
  return (
    sign +
    meta.symbol +
    Math.abs(v).toLocaleString(meta.locale, {
      minimumFractionDigits: meta.decimals,
      maximumFractionDigits: meta.decimals,
    })
  );
}

export function fmtCompactMoney(n) {
  if (n == null || isNaN(n)) return (CURRENCY_META[_currency]?.symbol || "$") + "0";
  const meta = CURRENCY_META[_currency] || CURRENCY_META.USD;
  const v = Number(n);
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1_000_000) return sign + meta.symbol + (abs / 1_000_000).toFixed(1) + "M";
  if (abs >= 1_000) return sign + meta.symbol + (abs / 1_000).toFixed(1) + "k";
  return sign + meta.symbol + abs.toFixed(0);
}

// ------------- Date format preference -------------
// Module-level singleton so any component can read/write without a Context
// listener. AuthContext calls `setDateFormat` on login/refresh; UI reads via
// `getDateFormat()` or picks up the format via the `fmtDate` helper.
let _dateFormat = (typeof localStorage !== "undefined" && localStorage.getItem("cashflow.date_format")) || "mm/dd/yyyy";
const _dateFormatListeners = new Set();

export function getDateFormat() {
  return _dateFormat;
}

export function setDateFormat(fmt) {
  if (!fmt || fmt === _dateFormat) return;
  _dateFormat = fmt;
  try { localStorage.setItem("cashflow.date_format", fmt); } catch { /* noop */ }
  _dateFormatListeners.forEach((cb) => cb(fmt));
}

export function subscribeDateFormat(cb) {
  _dateFormatListeners.add(cb);
  return () => _dateFormatListeners.delete(cb);
}

/**
 * Format any date-like input using the user's preferred format. Accepts:
 *   - ISO YYYY-MM-DD (bare date string from the API)
 *   - ISO timestamps (2026-02-23T14:30:00Z)
 *   - Date objects
 * Returns "—" for falsy input.
 */
export function fmtDate(input) {
  if (!input) return "—";
  let yyyy;
  let mm;
  let dd;
  if (typeof input === "string") {
    const m = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      [, yyyy, mm, dd] = m;
    }
  }
  if (!yyyy) {
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d)) return typeof input === "string" ? input : "—";
    yyyy = String(d.getFullYear());
    mm = String(d.getMonth() + 1).padStart(2, "0");
    dd = String(d.getDate()).padStart(2, "0");
  }
  switch (_dateFormat) {
    case "dd/mm/yyyy":
      return `${dd}/${mm}/${yyyy}`;
    case "yyyy-mm-dd":
      return `${yyyy}-${mm}-${dd}`;
    case "mm/dd/yyyy":
    default:
      return `${mm}/${dd}/${yyyy}`;
  }
}

/** Date + time using the user's preferred date format + h:mm AM/PM. */
export function fmtDateTime(input) {
  if (!input) return "—";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d)) return typeof input === "string" ? input : "—";
  const date = fmtDate(d);
  let h = d.getHours();
  const min = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${date} ${h}:${min} ${ampm}`;
}
